import { clone, EComponentTypes, IdHelper, IComponentDictionary, IUiElement, IComponent, IBranch, ISearchLocation, IControl, IAction, ISharedJourney } from '../../helpers/';
import { ACTIVE_ELEMENT_TYPES } from '../../constants';
import types from '../types';
import store from '../store';

interface IActiveProcessState {
	elements: { [id: string]: IUiElement };
	shownElements: string[];
}

const initialState: IActiveProcessState = {
	elements: {},
	shownElements: []
};

const activeProcessReducer = function (state: IActiveProcessState = initialState, action): IActiveProcessState {

	let stateClone: IActiveProcessState = clone(state);

	switch (action.type) {
		case types.SET_ACTIVE_COMPONENTS:
			stateClone = setActiveComponents(stateClone, action.components);
			break;

		case types.UPDATE_ACTIVE_COMPONENTS:
			stateClone = updateActiveComponents(stateClone, action.componentId, action.component);
			break;

		case types.SET_NEXT_ACTIVE_ELEMENT:
			stateClone = setNextComponentId(stateClone, action.element, action.elementId, action.nextElementId, action.componentId, action.nextComponentId);
			break;

		case types.ADD_ACTIVE_COMPONENT:
			stateClone = addActiveComponent(stateClone, action.previousComponentId, action.loopComponentId, action.newComponentId, action.componentType, action.components);
			break;

		case types.DELETE_ACTIVE_COMPONENT:
			stateClone = deleteActiveComponent(stateClone, action.componentId);
			break;

		case types.DELETE_LOOP_ACTIVE_COMPONENT:
			stateClone = deleteLoopActiveComponents(stateClone, action.componentId);
			break;

		case types.SET_ACTIVE_SEARCH_LOCATION:
			stateClone = setActiveSearchLocation(stateClone, action.elementId);
			break;

		case types.CLOSE_SEARCH_LOCATION:
			stateClone = removeSearchBoxes(stateClone, action.searchLocation);
			break;

		case types.DELETE_BRANCH:
			stateClone = deleteCondition(stateClone, action);
			break;

		default:
			break;
	}

	return stateClone;

};

const getBranchElements = (condition: IBranch, decisionNextComponentId: string, branchMergeIconId: string, components: IComponentDictionary): IUiElement[] => {

	let elements: IUiElement[] = [];
	let { NextComponent } = condition;

	if (!NextComponent || !NextComponent.ComponentId)
		return elements;

	let component: IComponent = components[NextComponent.ComponentId];

	while (component) {

		elements = elements.concat(getElements(component, components));

		if (component.NextComponent && component.NextComponent.ComponentId !== decisionNextComponentId)
			component = components[component.NextComponent.ComponentId];

		else
			component = null;

	}

	// Set the branch icon to be next element of last elements of every conditions
	return elements.map(element => {

		if (element.nextElementId === decisionNextComponentId || !element.nextElementId)
			element.nextElementId = branchMergeIconId;

		return element;

	});

};

const getDecisionAddButtonId = (Id: string, index: number): string => {
	return Id + '_' + index + '_ADD';
}

const getDecisionUIElements = (Id: string, mergeComponentNextButtonId: string, components?: IComponentDictionary): IUiElement[] => {

	let elements: IUiElement[] = [];

	let branchSelectorId: string = Id + "_SELECTOR";
	let branchMergeIconId: string = Id + "_MERGE";
	let buttonId: string = mergeComponentNextButtonId;
	let branchAddButtons: IUiElement[];

	if (components) {

		let component = components[Id];

		// Generate all branches add buttons
		branchAddButtons = component.Decision.Branches.map((condition, i) => {

			let nextId: string = condition.NextComponent && condition.NextComponent.ComponentId || branchMergeIconId;
			let element: IUiElement = {
				type: ACTIVE_ELEMENT_TYPES.Add,
				elementId: getDecisionAddButtonId(Id, i),
				nextElementId: nextId,
				componentId: Id
			};

			return element;

		});

		// Add all add buttons
		elements = elements.concat(branchAddButtons);

		let nextElementId: string = component && component.NextComponent && component.NextComponent.ComponentId
			? component.NextComponent.ComponentId : null;

		// For every condition in a decision, get the condition next component and loop through the next components until the Decision next component is found		
		let branchElements: IUiElement[][] = component.Decision.Branches.map(c => getBranchElements(c, nextElementId, branchMergeIconId, components));

		// Add all branch elements to active elements
		branchElements.forEach(be => elements = elements.concat(be));

	}
	else {

		// this is a new decision, so add an initial branch element
		elements.push({
			type: ACTIVE_ELEMENT_TYPES.Add,
			elementId: getDecisionAddButtonId(Id, 0),
			nextElementId: branchMergeIconId,
			componentId: Id
		});

	}

	// Decision component
	elements.push({
		type: ACTIVE_ELEMENT_TYPES.Component,
		elementId: Id,
		nextElementId: branchSelectorId,
		componentId: Id
	});

	// Branch selector
	elements.push({
		type: ACTIVE_ELEMENT_TYPES.BranchSelector,
		elementId: branchSelectorId,
		nextElementId: branchAddButtons ? branchAddButtons[0].elementId : getDecisionAddButtonId(Id, 0),
		componentId: Id
	});

	// Branch merging
	elements.push({
		type: ACTIVE_ELEMENT_TYPES.MergeIcon,
		elementId: branchMergeIconId,
		nextElementId: buttonId,
		componentId: Id
	});

	return elements;

}

const getLoopUIElements = (Id: string, loopEndNextButtonId: string, components?: IComponentDictionary): IUiElement[] => {

	let elements: IUiElement[] = [];

	let loopAddButtonId = Id + "_ADD";
	let loopEndId = Id + "_END";

	// Loop Component
	elements.push({
		type: ACTIVE_ELEMENT_TYPES.Component,
		elementId: Id,
		nextElementId: loopAddButtonId,
		componentId: Id
	});

	if (components && components[Id].Loop && components[Id].Loop.FirstLoopComponent && components[Id].Loop.FirstLoopComponent.ComponentId) {

		// existing loop component

		let loopComponent: IComponent = components[Id];

		// Loop Add
		elements.push({
			type: ACTIVE_ELEMENT_TYPES.LoopAdd,
			elementId: loopAddButtonId,
			nextElementId: loopComponent.Loop.FirstLoopComponent.ComponentId,
			componentId: Id,
			loopComponentId: Id
		});


		// add inner elements
		let innerComponent = components[loopComponent.Loop.FirstLoopComponent.ComponentId];

		while (innerComponent) {
			let innerElements = getElements(innerComponent, components);

			if (innerElements.length) {

				for (let i = 0; i < innerElements.length; i++) {
					innerElements[i].loopComponentId = loopComponent.Id;
					elements.push(innerElements[i]);
				}

			}

			innerComponent = innerComponent.NextComponent && innerComponent.NextComponent.ComponentId ? components[innerComponent.NextComponent.ComponentId] : null;
		}

		elements[elements.length - 1].nextElementId = loopEndId;

	}
	else {

		// new loop component

		// Loop Add
		elements.push({
			type: ACTIVE_ELEMENT_TYPES.LoopAdd,
			elementId: loopAddButtonId,
			nextElementId: loopEndId,
			componentId: Id,
			loopComponentId: Id
		});

	}

	// Loop End
	elements.push({
		type: ACTIVE_ELEMENT_TYPES.LoopEnd,
		elementId: loopEndId,
		nextElementId: loopEndNextButtonId,
		componentId: Id,
		loopComponentId: Id
	});

	return elements;

}

const getElements = (component: IComponent, components: IComponentDictionary): IUiElement[] => {

	let elements: IUiElement[] = [];
	let { Id } = component;
	let typeName: string = component.ComponentType.Name;
	let buttonId: string = IdHelper.newGuid();
	let nextElementId: string = component && component.NextComponent && component.NextComponent.ComponentId
		? component.NextComponent.ComponentId
		: null;

	switch (typeName) {

		case 'Decision':
			elements = getDecisionUIElements(Id, buttonId, components);
			break;

		case 'Loop':
			elements = getLoopUIElements(Id, buttonId, components);
			break;

		default:
			// Other components
			elements.push({
				type: ACTIVE_ELEMENT_TYPES.Component,
				elementId: Id,
				nextElementId: buttonId,
				componentId: Id,
				loopComponentId: component.ParentLoopId
			});
			break;

	}

	// Add button
	elements.push({
		type: ACTIVE_ELEMENT_TYPES.Add,
		elementId: buttonId,
		nextElementId: nextElementId,
		componentId: Id
	});

	return elements;

};

const addElement = (state: IActiveProcessState, element: IUiElement): IActiveProcessState => {

	if (!state.elements[element.elementId])
		state.elements[element.elementId] = element;

	return state;

};

let setActiveComponents = (state: IActiveProcessState, components: IComponentDictionary): IActiveProcessState => {

	state.elements = {};

	for (let componentId in components) {

		let component: IComponent = components[componentId];
		let elements: IUiElement[] = getElements(component, components);

		elements.forEach(element => state = addElement(state, element));

	}

	return state;

};

let updateActiveComponents = (state: IActiveProcessState, deletedOutputs: string[], component: IControl | IAction | ISharedJourney): IActiveProcessState => {

	let componentOutput = component.OutputValues;

	// state. = {};
	// let allComponentOutputs = store.getState().outputsState.outputs.filter(x => x.componentId === outputs);
	// let currentComponentOutputsId = componentOutput.map(x => x.Id);
	// let deletedOutputs = allComponentOutputs.filter(x => currentComponentOutputsId.indexOf(x.Id) < 0).map(x => x.Id);


	let processState = store.getState().processState;
	let jsonJourney = JSON.stringify(processState.components);
	deletedOutputs.forEach(x => {
		jsonJourney = replaceAll(jsonJourney, "##" + x + "##", "");
	})
	const newProcessState = JSON.parse(jsonJourney);
	store.getState().processState.components = newProcessState;

	function replaceAll(str: string, find: string, replace: string) {
		return str.replace(new RegExp(find, 'g'), replace);
	}


	store.dispatch({
		type: types.SET_ACTIVE_COMPONENTS,
		outputs: newProcessState
	});

	return state;

};

let setNextComponentId = (state: IActiveProcessState, element: string, elementId: string, nextElementId: string, componentId: string, nextComponentId: string): IActiveProcessState => {
	// If we're switching to a branch, that has never been visible, add an "_ADD" button, which merges to "_MERGE"
	if (nextElementId && nextElementId.indexOf("_ADD") > -1 && typeof state.elements[nextElementId] === "undefined") {
		state.elements[nextElementId] = {
			type: ACTIVE_ELEMENT_TYPES.Add,
			elementId: nextElementId,
			nextElementId: componentId + "_MERGE",
			componentId: componentId
		};
	}

	// If there is no next element, ensure an Add button is generated
	if (nextElementId === null) {
		nextElementId = IdHelper.newGuid();

		state.elements[nextElementId] = {
			type: ACTIVE_ELEMENT_TYPES.Add,
			elementId: nextElementId,
			nextElementId: nextComponentId,
			componentId: componentId
		};
	}

	// Set the next element id
	state.elements[elementId] = {
		type: element,
		elementId: elementId,
		nextElementId: nextElementId,
		componentId: componentId
	};

	return state;
};

const getActiveSearchBoxId = (state: IActiveProcessState, previousElementId: string): string => {

	let hasFoundSearchBox: boolean = false;

	// Loop through the elements untill we find the search box. This is usually the "nextElementId" of the component, but could be further down the chain. For example, a decision component will have a branch selector between the component and the add/search element
	while (!hasFoundSearchBox) {

		let element: IUiElement = previousElementId && state.elements[previousElementId] && state.elements[previousElementId].nextElementId
			? state.elements[state.elements[previousElementId].nextElementId]
			: null;

		if (element && element.type === ACTIVE_ELEMENT_TYPES.Search || !element) {
			hasFoundSearchBox = true;
		} else {
			previousElementId = element.elementId;
		}

	}

	let activeSearchboxId = previousElementId && state.elements[previousElementId] && state.elements[previousElementId].nextElementId
		? state.elements[previousElementId].nextElementId
		: null;

	return activeSearchboxId;

};

let addActiveComponent = (state: IActiveProcessState, previousComponentId: string, loopComponentId: string, newComponentId: string, componentTypeId: number,
	components: IComponentDictionary): IActiveProcessState => {

	let activeSearchboxId = getActiveSearchBoxId(state, previousComponentId);

	// The element after the "activeSearchBox" should be the next component
	let nextComponentId = activeSearchboxId && state.elements[activeSearchboxId] && state.elements[activeSearchboxId].nextElementId ? state.elements[activeSearchboxId].nextElementId : null;

	let searchId = IdHelper.newGuid();

	if (activeSearchboxId) {
		state.elements[activeSearchboxId].type = ACTIVE_ELEMENT_TYPES.Add;
		state.elements[activeSearchboxId].nextElementId = newComponentId;
	}

	if (componentTypeId === EComponentTypes.decision) {
		let decisionElements = getDecisionUIElements(newComponentId, searchId);
		decisionElements.forEach(element => {
			if (loopComponentId) {
				element.loopComponentId = loopComponentId;
			}
			state = addElement(state, element)
		});
	}
	else if (componentTypeId === EComponentTypes.loop) {
		let loopElements = getLoopUIElements(newComponentId, searchId)
		loopElements.forEach(element => state = addElement(state, element));
	}
	else {
		state.elements[newComponentId] = {
			type: ACTIVE_ELEMENT_TYPES.Component,
			elementId: newComponentId,
			nextElementId: searchId,
			componentId: newComponentId,
			loopComponentId: loopComponentId
		};
	}

	state.elements[searchId] = {
		type: ACTIVE_ELEMENT_TYPES.Search,
		elementId: searchId,
		nextElementId: nextComponentId,
		componentId: newComponentId,
		loopComponentId: loopComponentId
	};

	return state;
};

let deleteActiveComponent = (state: IActiveProcessState, componentId: string): IActiveProcessState => {

	let nextElementId = state.elements[componentId].nextElementId;
	let previousElementId = getParentElement(state, componentId);

	// if the next element is a Branch Selector set the nextElementId to the merge point
	if (nextElementId && state.elements[nextElementId] && state.elements[nextElementId].type === ACTIVE_ELEMENT_TYPES.BranchSelector) {
		nextElementId = state.elements[componentId + "_MERGE"].nextElementId;
	};

	// if the next element is an Add button or Search, delete that also
	if (nextElementId && state.elements[nextElementId] && (state.elements[nextElementId].type === ACTIVE_ELEMENT_TYPES.Add || state.elements[nextElementId].type === ACTIVE_ELEMENT_TYPES.LoopAdd || state.elements[nextElementId].type === ACTIVE_ELEMENT_TYPES.Search)) {
		let tempNextElId = state.elements[nextElementId].nextElementId;
		delete state.elements[nextElementId];
		nextElementId = tempNextElId;
	};

	if (previousElementId) {
		state.elements[previousElementId].nextElementId = nextElementId;
	};

	delete state.elements[componentId];

	return state;
};

let deleteLoopActiveComponents = (state: IActiveProcessState, componentId: string): IActiveProcessState => {

	let elementBeforeLoopId = getParentElement(state, componentId);

	let element = state.elements[componentId];
	let nextElementId;

	// delete all elements inside the loop
	while (element.type !== ACTIVE_ELEMENT_TYPES.LoopEnd) {
		nextElementId = element.nextElementId;
		delete state.elements[element.elementId];
		element = state.elements[nextElementId];
	}

	// delete the LoopEnd, 
	nextElementId = element.nextElementId;
	delete state.elements[element.elementId];

	// ...and the element that follows, which will be either add or search
	element = state.elements[nextElementId];
	nextElementId = element.nextElementId;
	delete state.elements[element.elementId];

	let elementAfterLoopEndId = nextElementId; //store the id of the element after the loop

	// set the next element of the element before the loop to be the element before the loop
	state.elements[elementBeforeLoopId].nextElementId = elementAfterLoopEndId;

	return state;

};

let setActiveSearchLocation = (state: IActiveProcessState, elementId: string): IActiveProcessState => {

	state = removeSearchBoxes(state);

	if (elementId)
		state.elements[elementId].type = ACTIVE_ELEMENT_TYPES.Search;

	return state;
};

const removeSearchBoxes = (state: IActiveProcessState, searchLocation?: ISearchLocation): IActiveProcessState => {

	for (let id in state.elements) {

		if (state.elements[id].type === ACTIVE_ELEMENT_TYPES.Search && searchLocation && searchLocation.loopComponentId) {
			state.elements[id].type = ACTIVE_ELEMENT_TYPES.LoopAdd;
		}
		else if (state.elements[id].type === ACTIVE_ELEMENT_TYPES.Search) {
			state.elements[id].type = ACTIVE_ELEMENT_TYPES.Add;
		}

	}

	return state;

};

let getParentElement = (state: IActiveProcessState, elementId: string): string => {

	for (var id in state.elements) {
		if (state.elements[id].nextElementId === elementId) {
			return id;
		}
	}

	return null;
};

let deleteCondition = (state: IActiveProcessState, action) => {
	// When a condition is deleted, make sure the branch selector does not point to the deleted condition
	let component = state.elements[action.componentId];
	let selector = state.elements[component.nextElementId];

	selector.nextElementId = action.componentId + "_0_ADD";

	return state;
};

export default activeProcessReducer;


// WEBPACK FOOTER //
// ./source/ts/store/reducers/process-active.ts